import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import ApiService from "../services/ApiService";
import { StandardPageTitle } from "../themes/StandardHeader";

const base64Encode = (str) => btoa(unescape(encodeURIComponent(str)));

const StaffChangePassword = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { profile, sessionId } = useContext(AuthContext) || {};
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = "กรุณากรอกรหัสผ่านปัจจุบัน";
    }
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = "กรุณากรอกรหัสผ่านใหม่";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร";
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "กรุณายืนยันรหัสผ่านใหม่";
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = "รหัสผ่านไม่ตรงกัน";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await ApiService({
        method: "POST",
        url: "/changeUserPassword",
        data: {
          userID: profile?.DLDEmployeeID,
          currpassword: base64Encode(formData.currentPassword),
          newpassword: base64Encode(formData.newPassword),
        },
        sessionId,
      });

      if (response.result === "success") {
        toast({
          title: "เปลี่ยนรหัสผ่านสำเร็จ",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigate("/profile");
      } else {
        toast({
          title: response.error || "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: error.message || "เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
      <Box maxW="560px" mx="auto">
        <StandardPageTitle title="เปลี่ยนแปลงรหัสผ่าน" />
        <Card mt={4}>
          <CardHeader>
            <Heading size="md">ตั้งรหัสผ่านใหม่</Heading>
            <Text fontSize="sm" color="gray.600" mt={2}>
              กรอกรหัสผ่านปัจจุบันและรหัสผ่านใหม่ของคุณ
            </Text>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={5} align="stretch">
              <FormControl isRequired isInvalid={!!errors.currentPassword}>
                <FormLabel>รหัสผ่านปัจจุบัน</FormLabel>
                <Input
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.currentPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.newPassword}>
                <FormLabel>รหัสผ่านใหม่</FormLabel>
                <Input
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
                <FormHelperText color="gray.500">
                  กำหนดรหัสผ่าน ความยาวไม่น้อยกว่า 6 ตัวอักษร
                </FormHelperText>
                <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.confirmPassword}>
                <FormLabel>ยืนยันรหัสผ่านใหม่</FormLabel>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>

              <Flex gap={3} justify="flex-end" pt={2}>
                <Button variant="outline" onClick={() => navigate("/profile")}>
                  ยกเลิก
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  loadingText="กำลังบันทึก..."
                >
                  บันทึกรหัสผ่าน
                </Button>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export default StaffChangePassword;
